import { useState, useEffect } from "react";
import {
  VStack,
  Box,
  Text,
  InputRightElement,
  InputGroup,
  useColorModeValue,
  Input,
  Center,
  Button,
  Heading,
  ListItem,
  Image,
  UnorderedList,
  Flex,
  Checkbox,
  Tooltip,
} from "@chakra-ui/react";
import PuffLoader from "react-spinners/PuffLoader";
import { layerStyle, textStyle, variant } from "theme/names";
import { CommentReplies } from "./comment-replies-composer";
import toast from "react-hot-toast";
import { getPostFromIdShort } from "controllers/engage";
import { BsFillArrowUpCircleFill } from "react-icons/bs";
import { BiRefresh, BiInfoCircle } from "react-icons/bi";
import { useSession } from "next-auth/react";
import {
  getAccount,
  getAuthUrl,
  updateUser,
} from "../../../utils/sessionHelper";

export const CommentsByURL = ({
  commentFetchLimists = 30,
  planType,
  isAuthenticated,
  isValidToken,
  idUser,
  isInfluentLeader = true,
  autoLike = false,
  onAutoLike,
}) => {
  const [nbCommentLimits, setNBCommentLimit] =
    useState<number>(commentFetchLimists);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [commentsData, setCommentsData] = useState<any>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [userPlanType, setUserPlanType] = useState<string>("");
  const [userIsAuthenticated, setUserIsAuthenticated] =
    useState<boolean>(isAuthenticated);
  const [isTokenValid, setIsTokenValid] = useState<boolean>(isValidToken);
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const [autoLikeLocal, setAutoLikeLocal] = useState<boolean>(false);
  const { data: session, status } = useSession() ?? {};
  const loading = status === "loading";
  const onClickFetchCommentsHandler = () => {
    setCommentsData([]);
    if (inputValue.includes("/pulse/")) {
      toast.error("Articles are not supported yet, please enter a post");
      return;
    }

    if (
      !inputValue ||
      (!inputValue.includes("/posts/") &&
        !inputValue.includes("/feed/update/urn:li:activity"))
    ) {
      toast.error("Invalid post or wrong url");
      return;
    }

    let idNumeric = "";

    if (inputValue.includes("urn:li:activity")) {
      let split = inputValue.split("?");
      split = split[0].split(":");
      idNumeric = split[split.length - 1];
      idNumeric = idNumeric.replace("/", "");
    } else {
      let split = inputValue.split("-");
      for (let i = split.length - 2; i >= 0; i--) {
        // console.log("testing: " + parseInt(split[i]));
        if (!idNumeric && parseInt(split[i])) {
          idNumeric = split[i];
        }
      }
      // idNumeric = split[split.length-2];
    }

    fetchPostAndComments(idNumeric);
  };

  const fetchPostAndComments = async (idShort) => {
    setIsLoading(true);
    setCommentsData([]);

    let post;
    let comments;

    try {
      //first try db and if not found then vendisys api
      post = await getPostFromIdShort(idShort);

      if (!post?.length || !post?.[0]) {
        console.log("querying vendi for postid:", idShort);
        const queryDataForPost = {
          idShort,
        };

        const resPost = await fetch(
          "https://us-central1-ez4cast.cloudfunctions.net/linkedinGrow-getPostFromIdShortAPI",
          {
            method: "POST",
            body: JSON.stringify(queryDataForPost),
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );

        // console.log(resPost, 'comments - post');

        let postReply = await resPost.json();

        if (!postReply?.success) throw "No post found.";

        post = postReply?.results;
      }

      if (!post?.[0]?.id || !post?.[0]?.idShort)
        throw "Post id or short id not found";

      const queryDataForComments = {
        linkedinShortPostId: post?.[0]?.idShort,
        idUser,
        dashEntityUrn: getAccount(session)?.dashEntityUrn,
        isInfluentLeader,
        planType: getAccount(session)?.subscription?.plan,
      };

      const resComment = await fetch(
        "https://us-central1-ez4cast.cloudfunctions.net/linkedinGrow-getPostComments",
        {
          method: "POST",
          body: JSON.stringify(queryDataForComments),
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      const commentReply: any = await resComment.json();
      //   console.log(reply, reply.results,'reply')
      if (commentReply?.success) {
        // console.log(commentReply, 'comments reply');
        comments = [...commentReply?.results?.slice(0, nbCommentLimits)];
      } else {
        throw "Couldn't fetch comments for post id:," + post?.id;
      }
    } catch (e) {
      toast.error("Error while fetching post/comments: " + e);
      console.log("Error fetching post and comments", e);
    }

    setCommentsData([
      {
        post: post?.[0] || {},
        comments: comments || [],
      },
    ]);

    setIsLoading(false);
    setHasFetched(true);
    // console.log(await res.json());
  };

  useEffect(() => {
    setUserIsAuthenticated(isAuthenticated);
    setAutoLikeLocal(autoLike);
  }, [isAuthenticated]);

  useEffect(() => {
    setIsTokenValid(isValidToken);
  }, [isValidToken]);

  if (planType != "enterprise" || !(userIsAuthenticated && isTokenValid)) {
    return <UpgradeToProComp />;
  }

  return (
    <VStack
      alignContent="center"
      justifyContent="center"
      mb={9}
      w={"full"}
      gap={6}
    >
      <Box>
        <Tooltip
          label={
            "Takes about 1 ~ 5 mins depending on the number of comments we need to fetch"
          }
          placement={"right"}
          hasArrow
          size="xs"
        >
          <Text
            textStyle={textStyle["body.medium.light"]}
            fontSize="md"
            textAlign={"center"}
          >
            Retrieves the last {nbCommentLimits} comments from your posts post.{" "}
            <BiInfoCircle
              style={{
                display: "inline-block",
                top: "3px",
                position: "relative",
              }}
            />
          </Text>
        </Tooltip>
      </Box>
      <Box width={"100%"}>
        <InputGroup size="lg" width={"100%"}>
          <Input
            value={inputValue}
            type={"text"}
            onChange={(e) => setInputValue(e.target.value)}
            isDisabled={isLoading}
            placeholder="https://www.linkedin.com/feed/update/urn:li:activity:6903292606512451584/"
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                onClickFetchCommentsHandler();
              }
            }}
          />
          <InputRightElement width={["150px", "200px"]}>
            <Button
              size={["xs", "sm"]}
              onClick={onClickFetchCommentsHandler}
              variant={"primary"}
              isLoading={isLoading}
              py={[3, 3]}
              px={[3, 5]}
            >
              Retrieve comments
            </Button>
          </InputRightElement>
        </InputGroup>
      </Box>
      <Box width="100%">
        <Center>
          <PuffLoader loading={isLoading} color={"#0A66C2"} />
        </Center>
        {!isLoading && commentsData?.length > 0 && (
          <Flex
            justifyContent={"end"}
            flexDirection={"column"}
            alignSelf={"end"}
          >
            <Flex
              p="4"
              layerStyle={layerStyle["bg.border.rounded"]}
              width={"fit-content"}
              alignSelf={"end"}
            >
              <Checkbox
                ml={1}
                onChange={async (e) => {
                  onAutoLike(autoLikeLocal);
                  setAutoLikeLocal(!autoLikeLocal);
                }}
                isChecked={autoLikeLocal}
              >
                auto-like post when replying
              </Checkbox>
            </Flex>
            <Flex mt="3" alignSelf={"end"}>
              <Button
                variant="primary"
                ml={5}
                isDisabled={!isAuthenticated}
                leftIcon={<BiRefresh />}
                onClick={onClickFetchCommentsHandler}
              >
                Refresh
              </Button>
            </Flex>
          </Flex>
        )}
        {commentsData?.length
          ? commentsData.map((commentPostobj, i) => {
              return (
                <CommentReplies
                  data={commentPostobj}
                  key={i}
                  loadingComments={false}
                />
              );
            })
          : !isLoading &&
            hasFetched && (
              <Text
                textStyle={textStyle["body.medium.light"]}
                fontSize="md"
                textAlign={"center"}
              >
                Post not found.
              </Text>
            )}
      </Box>
    </VStack>
  );
};

const UpgradeToProComp = () => {
  const headingColors = useColorModeValue("#333", "#ececec");
  const secondaryHeadingColors = useColorModeValue("#666", "#ececec");
  return (
    <VStack
      alignContent="center"
      justifyContent="center"
      w={"full"}
      gap={3}
      layerStyle="bgBorderRoundedHoverShadow"
      textStyle="body.medium.standard"
      px={9}
      py={6}
    >
      <Heading fontSize="2xl" color={headingColors} fontWeight={"bold"}>
        Unlock Post URL comment searches
      </Heading>
      <Text fontSize={"md"} textStyle={textStyle["body.medium.bold"]}>
        Search for comments on a specific post you or someone else has
        published.
      </Text>
      <Image
        src="/content/unlock-post-url-searches-reply-to-comments.png"
        alt="Update to pro plan"
        pb={3}
      />
      <Button
        leftIcon={<BsFillArrowUpCircleFill />}
        variant={"primary"}
        fontSize={"xl"}
        p={6}
        as={"a"}
        href={"/pricing"}
      >
        Upgrade to Pro
      </Button>
      <Text>starts at $199 USD/mo</Text>
      <Heading
        fontSize="xl"
        color={secondaryHeadingColors}
        alignSelf={"flex-start"}
      >
        What you get with a Pro plan:
      </Heading>
      <UnorderedList
        fontSize={"md"}
        textStyle="body.medium.standard"
        alignSelf={"flex-start"}
        pl={5}
        spacing={2}
      >
        <ListItem>
          Retrieve comments from any post URL and reply to them
        </ListItem>
        <ListItem>
          Increase the comment limit from the most recent 30 to the most recent
          500
        </ListItem>
        {/* <ListItem>
          Automatically import up to 500 likers & repliers per day into your
          contact lists
        </ListItem> */}
        <ListItem>Retrieve up to 500 posts to engage</ListItem>
      </UnorderedList>
      <Heading
        fontSize="lg"
        color={secondaryHeadingColors}
        alignSelf={"flex-start"}
      >
        We recommend the Pro plan for accounts with at least 10,000 followers.
      </Heading>
    </VStack>
  );
};
